import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface StructurePopoverProps {
  iupacName: string;
  parent: string;
  molecularWeight?: number;
  casNumber?: string;
  children: React.ReactNode;

  id?: string;
}

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const StructurePopover: React.FC<StructurePopoverProps> = ({
  iupacName,
  parent,
  molecularWeight,
  casNumber,
  children,
  id,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [copyAllStatus, setCopyAllStatus] = useState<"idle" | "copied">("idle");

  const [popoverId] = useState(
    () => id || `popover-${Math.random().toString(36).substr(2, 9)}`
  );

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleMouseEnterContainer = () => {
    clearHideTimer();
    if (containerRef.current) {
      document.dispatchEvent(
        new CustomEvent("structurePopoverOpened", { detail: { id: popoverId } })
      );

      const rect = containerRef.current.getBoundingClientRect();
      const popoverApproxWidth = 280;
      let leftPosition = rect.left + window.scrollX;
      if (leftPosition + popoverApproxWidth > window.innerWidth) {
        leftPosition = window.innerWidth - popoverApproxWidth - 10;
      }
      leftPosition = Math.max(10, leftPosition);
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 3,
        left: leftPosition,
      });
      setShowPopover(true);
    }
  };

  const handleMouseLeaveWithDelay = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 200);
  };

  const handleMouseEnterPopover = () => {
    clearHideTimer();
  };

  const handleCopyAll = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const lines: string[] = [];
    if (iupacName) lines.push(`IUPAC name: ${iupacName}`);
    if (parent) lines.push(`Parent: ${parent}`);
    if (molecularWeight !== undefined)
      lines.push(`Molecular Weight: ${molecularWeight} g/mol`);
    if (casNumber) lines.push(`CAS Number: ${casNumber}`);

    if (lines.length === 0) return;
    const textToCopy = lines.join("\n");

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyAllStatus("copied");
      setTimeout(() => setCopyAllStatus("idle"), 2000);
    } catch (err) {
      console.error("Failed to copy all text: ", err);
      alert("fail to copy all text");
    }
  };

  const popoverContent = (
    <div
      ref={popoverRef}
      onMouseEnter={handleMouseEnterPopover}
      onMouseLeave={handleMouseLeaveWithDelay}
      className="absolute z-[1000] p-3 text-xs bg-white border border-gray-200 rounded-lg shadow-xl w-auto max-w-xs"
      style={{
        top: popoverPosition.top,
        left: popoverPosition.left,
        minWidth: "200px",
      }}
    >
      {iupacName && (
        <p className="text-gray-800 mb-0.5">
          <strong className="font-semibold text-gray-900">IUPAC Name: </strong>
          {iupacName}
        </p>
      )}
      {parent && (
        <p className="text-gray-800 mb-0.5">
          <strong className="font-semibold text-gray-900">Parent: </strong>
          {parent}
        </p>
      )}
      {molecularWeight !== undefined && (
        <p className="text-gray-800 mb-0.5">
          <strong className="font-semibold text-gray-900">Mol. Weight: </strong>
          {molecularWeight} g/mol
        </p>
      )}
      {
        <p className="text-gray-800">
          <strong className="font-semibold text-gray-900">CAS Number: </strong>
          {casNumber ? casNumber : "N/A"}
        </p>
      }

      {(iupacName || parent || molecularWeight !== undefined || casNumber) && (
        <div className="mt-2.5 pt-2 border-t border-gray-200">
          <button
            onClick={handleCopyAll}
            className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-opacity-50 flex items-center justify-center transition-colors duration-150"
            type="button"
          >
            {copyAllStatus === "copied" ? (
              <>
                <CheckIcon /> <span className="ml-1">All Copied!</span>
              </>
            ) : (
              <>
                <CopyIcon /> <span className="ml-1">Copy All Info</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover) {
        setShowPopover(false);
      }
    };

    const handleOtherPopoverOpened = (event: Event) => {
      const customEvent = event as CustomEvent<{ id: string }>;
      if (customEvent.detail && customEvent.detail.id !== popoverId) {
        if (showPopover) {
          clearHideTimer();
          setShowPopover(false);
        }
      }
    };

    if (showPopover) {
      window.addEventListener("scroll", handleScroll, true);
    }

    document.addEventListener(
      "structurePopoverOpened",
      handleOtherPopoverOpened
    );

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener(
        "structurePopoverOpened",
        handleOtherPopoverOpened
      );
      clearHideTimer();
    };
  }, [showPopover, popoverId]);

  return (
    <div
      className="relative inline-block"
      ref={containerRef}
      onMouseEnter={handleMouseEnterContainer}
      onMouseLeave={handleMouseLeaveWithDelay}
    >
      {children}
      {showPopover && ReactDOM.createPortal(popoverContent, document.body)}
    </div>
  );
};

export default StructurePopover;
